import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  EventEmitter,
} from '@angular/core';
import { Constants } from './dashboard-constants';
import { SurveyStatus } from '../utils/survey-status';
import {
  DashboardPreference,
  SurveyListing,
} from '../shared/services/buyer-api/survey.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { BuyerApiService } from '../shared/services/buyer-api/survey.service';
import { MultiBuyerService } from '../../app/shared/services/multi-buyer/multi-buyer-service';
import { SupplierApiService } from '../shared/services/supplier-api/supplier-api.service';
import { AuthService } from '../auth/auth.service';
import { ModalService } from '@purespectrum1/ui/modal';
import {
  AppcuesConsts,
  AppcuesService,
  ChurnZeroConsts,
  ChurnZeroService,
} from '@purespectrum1/ui/tracking';
import { UserService } from '../operator/user-service/user.service';
import { USER_DASHBOARD_VIEW_OBJECT } from './dashboard-constants';
import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  forkJoin,
  iif,
  merge,
  Observable,
  from,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  observeOn,
  pluck,
  scan,
  share,
  shareReplay,
  skip,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { DashboardPreferenceService } from '../shared/services/dashboard-preference/dashboard-preference.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SocketService } from '../shared/services/socket/socket.service';
import { DashboardPreferenceState } from './domain/dashboard-preference';
import { DashboardPagination, EMPTY_VIEW } from './domain/dashboard-pagination';
import { DashboardState } from './domain/dashboard-state';
import { SearchView } from './survey-search/survey-search.component';
import {
  Counters,
  DashboardSearch,
  DashboardSource,
  TCCreateAction,
} from './types';
import { DashboardTab, TabsFactory, TabState } from './domain/dashboard-tab';
import { DashboardSurveyTransform } from './domain/dashboard-survey-transform';
import { Router } from '@angular/router';
import { SurveyPayloadService } from '../create-surveys/survey-payload.service';
import {
  CloneUnavailableTextResolver,
  InactiveQualificationsMessageComposer,
  TrafficChannelModalComponent,
  PROJECT_TYPES,
} from '@purespectrum1/ui';
import { BuyerService } from '../shared/services/buyer/buyer.service';
import { notifyMessage } from '../constants/notify-message';
import { TosComponent } from './tos/tos.component';
import { DashboardSelectedSurveys } from './domain/dasboard-survey-selected';
import { CommonService } from '../shared/services/common-service/common-service';
import { EachCountry } from '../shared/interfaces/common-service.interface';
import { DashboardClearSelectionConfirmation } from './dashboard-clear-selection-confirmation/dashboard-clear-selection-confirmation.component';
import {
  BuyerCombinedReport,
  CombinedReport,
  SupplierCombinedReport,
} from './bulk-edit/types';
import { ReportService } from '../buyer/download-reports/report-service/report.service';
import { CompanyInfo } from '../shared/interfaces/company.interface';
import { DashboardStateService } from './services/dashboard-state.service';
import { BuyerSurveyResponse } from '@purespectrum1/ui/marketplace/shared/interfaces/survey.interface';
import { DashboardSorting } from './domain/dashboard-sorting/dashboard-sorting';
import { DashboardFiltering } from './domain/dashboard-filtering/dashboard-filtering';
import { isEqual } from '../utils/deep-compare';
import { HttpContext } from '@angular/common/http';
import {
  ALLOW_INTERRUPTION,
  INTERRUPTION_GRACE_PERIOD,
  LoaderService,
} from '@purespectrum1/ui/loader';
import { catchTimeoutError } from '../shared/rxjs-operators/catch-timeout-error.operator';
import { SupplierSurveysResponse } from './survey-service/supplier-survey.interface';
import { TableStateEvent } from '@purespectrum1/ui/table';

@Component({
  selector: 'ps-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit, OnDestroy {
  public tabState: TabState = new TabState([]);
  public state$!: Observable<DashboardState>;
  public isMultiCountryWidget: boolean = false;
  public enableBackEndSortFilter$ = new BehaviorSubject<boolean>(false);
  public resetScroll = new EventEmitter<void>();
  public get tabIndex(): number {
    return this._dashboardStateService.lastTab;
  }

  public get tab(): DashboardTab {
    return this.tabState.tabs[this.tabIndex];
  }

  private _viewCommand$ = new Subject<SearchView>();
  private _paginationCommand$ = new Subject<DashboardPagination>();
  private _cloneCommand$ = new Subject<number>();
  private _deleteCommand$ = new Subject<number[]>();
  private _tcCommand$ = new Subject<TCCreateAction>();
  private _downloadCommand$ = new Subject<number>();
  private _bulkReportCommnad$ = new Subject<CombinedReport>();
  private _bulkEditCommand$ = new Subject<boolean>();
  private _companyCommand$ = new BehaviorSubject<CompanyInfo | undefined>(
    undefined
  );
  private readonly _restoreCommand$ = new Subject<void>();

  private _state!: DashboardState;
  private _pending: DashboardState = DashboardState.empty();
  private _completed: DashboardState = DashboardState.empty();
  private _completedTabIndex!: number;
  private _subscription: Subscription = new Subscription();
  private _useCache = false;
  private _surveyIds: number[] = [];
  private readonly _companyAndServiceBuyer$: Observable<{
    company: CompanyInfo | undefined;
    isServiceBuyer: boolean;
  }>;

  constructor(
    private _toastr: ToasterService,
    private _auth: AuthService,
    private _buyerApiService: BuyerApiService,
    private readonly _multiBuyerService: MultiBuyerService,
    private _supplierApiService: SupplierApiService,
    private _userService: UserService,
    private _churnZeroService: ChurnZeroService,
    private _dashboardPreferenceService: DashboardPreferenceService,
    private _deviceService: DeviceDetectorService,
    private _surveyPayloadService: SurveyPayloadService,
    private _buyerService: BuyerService,
    private _socketService: SocketService,
    private _commonService: CommonService,
    private _reportService: ReportService,
    private _dashboardStateService: DashboardStateService,
    private _modal: ModalService,
    private _router: Router,
    private _appcuesService: AppcuesService,
    private _loader: LoaderService
  ) {
    this._generateTabs();
    const index = this._dashboardStateService.lastTab;
    const status = this.tabState.tabs[index].status;

    this._state = DashboardState.initial(
      this._dashboardStateService.surveys.page,
      this._dashboardStateService.selected,
      status
    );

    this._companyAndServiceBuyer$ = combineLatest([
      this._companyCommand$,
      this._auth.loggedInAsServiceBuyer$,
    ]).pipe(
      map(([company, isServiceBuyer]) => {
        return {
          company: company || this._dashboardStateService.company,
          isServiceBuyer,
        };
      }),
      shareReplay(1)
    );
  }

  public ngOnInit(): void {
    this._auth.selectedMultiBuyerCompany$.subscribe((buyerEntity) => {
      this.initializeState();
    });

    // Subscriptions
    this._subscription.add(this._updateBackEndSortFilter());
    this._subscription.add(this._updateView());
    this._subscription.add(this._clone());
    this._subscription.add(this._tc());
    this._subscription.add(this._download());
    this._subscription.add(this._downloadBuyerBulkReport());
    this._subscription.add(this._downloadSupplierBulkReport());
    this._subscription.add(this._clearState());

    // others
    this._checkTOS();
    this._churnZeroService.sendEvent(
      ChurnZeroConsts.events.lastActive,
      ChurnZeroConsts.eventDescriptions.lastActive
    );
    this._appcuesService.sendEvent(AppcuesConsts.events.lastActive, {
      description: AppcuesConsts.eventDescriptions.lastActive,
    });
  }

  public initializeState() {
    const dashboard$ = this._dashboard();
    const countries$ = this._commonService.getCountriesList();
    const [page$, search$] = this._pagination();
    const sorting$ = this._sorting();
    const filtering$ = this._filtering();
    const [surveys$] = this._surveys(
      dashboard$,
      countries$,
      search$,
      page$,
      sorting$,
      filtering$
    );
    const socket$ = this._sockets();
    const selected$ = this._selected();

    const delete$ = this._deleteCommand$.pipe(
      map((ids) => this._state.delete(ids)),
      tap(() => {
        return this._dashboardStateService.surveys.cache({
          surveys: this._state.surveys,
          company: this._state.company,
        });
      })
    );

    const bulkEdit$ = this._bulkEditCommand$.pipe(
      filter((edited) => edited),
      map(() =>
        this._state.copyWith({ selected: DashboardSelectedSurveys.empty() })
      )
    );

    this.state$ = merge(surveys$, delete$, socket$, selected$, bulkEdit$).pipe(
      tap((state) => (this._state = state)),
      startWith(this._state)
    );
  }

  public ngOnDestroy(): void {
    // Unsubscribe when component is destroyed
    this._socketService.unsubscribeFromSurveys(this._surveyIds);
    this._subscription.unsubscribe();
  }

  public onSearch(value: string): void {
    this._dashboardStateService.search(value);
  }

  public onViewChange(view: SearchView): void {
    this._viewCommand$.next(view);
  }

  public onChangeTab(tab: number) {
    if (tab !== this.tabIndex) {
      this._dashboardStateService.tab(tab);
    }
  }

  public onPagination(): void {
    if (this._state.canLoad(this.tab)) {
      this._paginationCommand$.next(this._state.nextPage());
    }
  }

  public onClone(id: number): void {
    this._cloneCommand$.next(id);
  }

  public onDelete(id: number[]): void {
    this._deleteCommand$.next(id);
  }

  public onTC(tc: TCCreateAction): void {
    this._tcCommand$.next(tc);
  }

  public onDownloadReport(id: number): void {
    this._downloadCommand$.next(id);
  }

  public onSurveysSelected(surveys: SurveyListing[]): void {
    this._dashboardStateService.select(surveys);
  }

  public onBulkReport(event: CombinedReport): void {
    this._bulkReportCommnad$.next(event);
  }

  public onBulkEdit(event: boolean): void {
    this._bulkEditCommand$.next(event);
  }

  public onBeforeTabChange(): () => Observable<boolean> {
    return () => {
      if (this._state.selected.some()) {
        return this._modal
          .open(DashboardClearSelectionConfirmation)
          .onClose$.pipe(tap(() => this._dashboardStateService.select([])));
      }

      return of(true);
    };
  }

  public onCompanyChange(company: CompanyInfo) {
    this._dashboardStateService.clearSelectionOfActiveTab();
    this._dashboardStateService.company = company;
    this._companyCommand$.next(company);
  }

  private _selected(): Observable<DashboardState> {
    return this._dashboardStateService.selected$.pipe(
      map((selection) => this._state.select(selection))
    );
  }

  private _pagination(): [
    Observable<DashboardPagination>,
    Observable<DashboardPagination>
  ] {
    const service$: Observable<{ status: boolean; id: number }> =
      this._auth.loggedInAsServiceBuyer$.pipe(
        map((status) => ({ status, id: this._auth.serviceBuyer?.id ?? 0 }))
      );

    const isMultiBuyer$: Observable<boolean> =
      this._auth.loggedInAsMultiBuyer$.pipe(map((multiBuyer) => multiBuyer));

    const tab$: Observable<string> = this._dashboardStateService.tab$.pipe(
      map((tab) => String(this.tabState.tabs[tab].status)),
      tap(() => this._removeSurveyScore())
    );

    const company$: Observable<CompanyInfo | undefined> = this._companyCommand$;

    const searchText$: Observable<string> = this._dashboardStateService.search$;

    const view$: Observable<SearchView> = merge(
      this._view(),
      this._viewCommand$
    );

    const sources: DashboardSource = [
      company$,
      searchText$,
      view$,
      tab$,
      service$,
      isMultiBuyer$,
    ];

    const search$ = combineLatest(sources).pipe(
      scan((pagination, values: DashboardSearch) => {
        const [company, search, view, tab, service, isMultiBuyer] = values;

        const selectedCompanyId = this._dashboardStateService.company?.id;

        return pagination.copyWith({
          search,
          view,
          status: tab,
          isServiceBuyerRequest: service.status,
          isMultiBuyerRequest: isMultiBuyer,
          buyerId: company?.id || selectedCompanyId || service.id,
        });
      }, DashboardPagination.first()),
      share()
    );

    const page$ = this._paginationCommand$.pipe(
      withLatestFrom(search$),
      map(([pagination, search]) => search.copyWith({ page: pagination.page })),
      tap((pagination) =>
        this._dashboardStateService.surveys.cache({
          page: pagination.page,
        })
      )
    );

    return [page$, search$];
  }

  private _sorting(): Observable<DashboardSorting> {
    const sorting$ = this._dashboardStateService.state$.pipe(
      pluck('sort'),
      startWith(DashboardSorting.empty().sort),
      distinctUntilChanged((previousSort, newSort) =>
        isEqual(previousSort, newSort)
      ),
      map((sort) => DashboardSorting.from(sort)),
      tap(() => this._resetPaginationIfNotFirstPage()),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.enableBackEndSortFilter$.pipe(
      switchMap((enabled) => {
        if (enabled) {
          return sorting$;
        }

        return of(DashboardSorting.empty());
      }),
      startWith(DashboardSorting.empty())
    );
  }

  private _filtering(): Observable<DashboardFiltering> {
    const filtering$ = this._dashboardStateService.state$.pipe(
      pluck('filter'),
      distinctUntilChanged(
        (previousFilter, newFilter) =>
          JSON.stringify(previousFilter) === JSON.stringify(newFilter)
      ),
      map((filters) => DashboardFiltering.from(filters)),
      tap(() => this._resetPaginationIfNotFirstPage()),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.enableBackEndSortFilter$.pipe(
      switchMap((enabled) => {
        if (enabled) {
          return filtering$;
        }

        return of(DashboardFiltering.empty());
      }),
      startWith(DashboardFiltering.empty())
    );
  }

  private _surveys(
    config$: Observable<DashboardPreferenceState>,
    countries$: Observable<EachCountry[]>,
    search$: Observable<DashboardPagination>,
    page$: Observable<DashboardPagination>,
    sorting$: Observable<DashboardSorting>,
    filtering$: Observable<DashboardFiltering>
  ): [
    Observable<DashboardState>,
    Observable<DashboardState>,
    Observable<Counters>
  ] {
    const pagination$ = merge(page$, search$).pipe(
      map((pagination) => {
        const page = this._useCache
          ? this._dashboardStateService.surveys.page
          : pagination.page;
        return pagination.copyWith({ page });
      }),
      startWith(DashboardPagination.first())
    );

    const fetch$ = this._fetchSurveys(
      config$,
      countries$,
      pagination$,
      sorting$,
      filtering$
    );

    const count$ = combineLatest([search$, filtering$]).pipe(
      switchMap(([pagination, filtering]) =>
        this._count(pagination, filtering)
      ),
      startWith([]),
      share()
    );

    const surveys$ = combineLatest([fetch$, count$]).pipe(
      tap(([_, counters]) => this.tabState.update(counters)),
      map(([state, _]) => state)
    );

    return [surveys$, fetch$, count$];
  }

  private _fetchSurveys(
    config$: Observable<DashboardPreferenceState>,
    countries$: Observable<EachCountry[]>,
    pagination$: Observable<DashboardPagination>,
    sorting$: Observable<DashboardSorting>,
    filtering$: Observable<DashboardFiltering>
  ): Observable<DashboardState> {
    const update$ = combineLatest([
      config$,
      countries$,
      pagination$,
      sorting$,
      filtering$,
    ]).pipe(
      debounceTime(50),
      scan((state, [config, countries, pagination, sorting, filtering]) => {
        return state.copyWith({
          preferences: config.copyWith({
            serviceOperator: pagination.isServiceBuyerRequest,
          }),
          pagination: pagination,
          countries: countries,
          surveys: this._state.surveys,
          company: config?.company,
          sorting,
          filtering,
        });
      }, this._state),
      share()
    );

    const restore$ = merge(
      this._loader.cancelRequests$,
      this._restoreCommand$
    ).pipe(
      map(() => ({ event: this._hasTabChanged() ? 'reset' : 'revert' })),
      share()
    );

    const reset$ = restore$.pipe(
      filter(({ event }) => event === 'reset'),
      debounceTime(50),
      switchMap(() => this._resetState())
    );

    const revert$ = restore$.pipe(
      filter(({ event }) => event === 'revert'),
      switchMap(() => this._revertState())
    );

    const shouldUpdate$ = update$.pipe(
      skip(1),
      map((state) => {
        const updateRestrictions = [
          () => isEqual(this._completed.preferences, state.preferences),
          () => isEqual(this._params(this._completed), this._params(state)),
        ];
        // If the data is already loaded so we won't make an API request
        if (updateRestrictions.every((restriction) => restriction())) {
          return false;
        }
        return true;
      }),
      startWith(true)
    );

    const bulkEditState$ = this._bulkEditCommand$.pipe(
      filter((edited) => edited),
      map(() => this._state)
    );

    const shouldFetch$ = merge(
      shouldUpdate$,
      reset$.pipe(mapTo(true)),
      revert$.pipe(mapTo(false)),
      bulkEditState$.pipe(mapTo(true))
    );

    return merge(update$, reset$, revert$, bulkEditState$).pipe(
      withLatestFrom(shouldFetch$),
      filter(([, shouldFetch]) => shouldFetch),
      map(([state]) => state),
      switchMap((state) => {
        this._pending = state;
        return this._fetch(state);
      }),
      map((state) =>
        state.copyWith({
          selected:
            this._state.pagination.status == state.pagination.status
              ? this._state.selected
              : DashboardSelectedSurveys.empty(),
        })
      ),
      share()
    );
  }

  private _dashboard(): Observable<DashboardPreferenceState> {
    const userId = this._auth.user?.id || 0;
    const supplier$ = of(
      Constants.SUPPLIER_SURVEY_LISTING_TABLE_HEADERS as DashboardPreference[]
    ).pipe(observeOn(asyncScheduler));

    const buyer$ = this._dashboardPreferenceService.getDashboardPreference(
      userId,
      this._auth.isLoggedInAsServiceBuyer(),
      this._auth.isLoggedInAsMultiBuyer()
    );
    const enableBulkEditAndSorting$ = this._isBulkEditAndSortingEnabled();
    const dashboardPref$ = combineLatest([
      this._companyCommand$,
      enableBulkEditAndSorting$,
      iif(() => this._auth.userType === 'supplier', supplier$, buyer$),
      this._dashboardStateService.tab$,
    ]);
    return dashboardPref$.pipe(
      catchError((error) => {
        this._toastr.error(error.error.msg);
        return of([]);
      }),
      map(([company, enableBulkEditAndSorting, response]) => {
        return DashboardPreferenceState.from(
          response,
          this._deviceService.isMobile(),
          this._auth.userType,
          !!this._auth.buyerConfig?.enableDynamicDash ||
            this._auth.user?.operatorAcssLvls !== 'none',
          this._auth.buyerConfig?.enableTrafficChannels ?? false,
          this._auth.isLoggedInAsServiceBuyer(),
          enableBulkEditAndSorting,
          this._auth.buyerConfig?.enableMultiCountry ?? false,
          this._auth.buyerConfig?.enableTracker ?? false,
          this._auth.buyerConfig?.enableHealthMetrics ?? false,
          this._auth.buyerConfig?.enablePaceTool ?? false,
          this._rygSurveyScore(),
          company
        );
      }),
      tap((data) => {
        this.isMultiCountryWidget = !!data.columns.find(
          (column) => column.key === 'multiCountry'
        );
      })
    );
  }

  // it will remove the filter and sort if the tab is not eligible for RYG survey score
  _removeSurveyScore() {
    if (!this._rygSurveyScore()) {
      const state = this._dashboardStateService.lastState;
      const updatedFilter = state.filter.filter(
        (f) => f.field !== 'scorePercentage'
      );
      state.sort.type =
        state.sort.field === 'scorePercentage' ? 'none' : state.sort.type;
      this._dashboardStateService.update({
        ...state,
        filter: updatedFilter,
      });
    }
  }

  private _rygSurveyScore(): boolean {
    const isEligibleTab = Constants.ELIGIBLE_TABS_FOR_RYG_SURVEY_SCORE.includes(
      this.tab?.title
    );
    return (
      isEligibleTab &&
      (!!this._auth.buyerConfig?.enableRYGSurveyScore ||
        this._auth.userType === 'operator')
    );
  }

  private _isBulkEditAndSortingEnabled(): Observable<boolean> {
    return this._companyAndServiceBuyer$.pipe(
      map(({ company, isServiceBuyer }) => {
        if (this._auth.userType === 'buyer' || isServiceBuyer) {
          const bulkEditFeature =
            this._auth.buyerConfig?.enableFilteringSortingBulkEdit ?? false;
          const enabledToServiceOperator = isServiceBuyer && bulkEditFeature;
          const enabledToOperatorOrBuyer =
            !!this._dashboardStateService.company || bulkEditFeature;
          this._generateTabs();
          return enabledToServiceOperator || enabledToOperatorOrBuyer;
        }

        return !!company || !!this._dashboardStateService.company;
      })
    );
  }

  private _view(): Observable<SearchView> {
    const userId = this._auth.user?.id || 0;
    const supplier$ = of(EMPTY_VIEW).pipe(observeOn(asyncScheduler));

    const buyer$ = this._userService.getUserViewSelection(userId).pipe(
      map((response) =>
        this._userService.setSurveyFilterOptions(response, userId)
      ),
      map((options) => ({
        option: 0,
        filter:
          this._auth.userType === 'buyer'
            ? options.buyerFilterObject
            : options.serviceOperatorFilterObject,
        managers: options.projectManagerObject,
      })),
      catchError((error) => {
        this._toastr.error(error.error.msg);
        return of(EMPTY_VIEW);
      })
    );

    return iif(() => this._auth.userType === 'supplier', supplier$, buyer$);
  }

  private _fetch(state: DashboardState): Observable<DashboardState> {
    const supplier$ = this._supplierApiService
      .getSupplierSurveys(
        state.pagination.status as SurveyStatus,
        state.pagination.page,
        state.pagination.search
      )
      .pipe(
        catchTimeoutError(() => {
          this._toastr.error(Constants.ERROR_MESSAGE.TIMEOUT);
          return of({ surveys: [] } as unknown as SupplierSurveysResponse);
        }),
        map((response) => response.surveys)
      );

    const params = this._params(state);
    const context = this._getSurveysRequestContext(state);
    const buyer$ = this._buyerApiService.getSurveys(params, context).pipe(
      catchTimeoutError(() => {
        this._toastr.error(Constants.ERROR_MESSAGE.TIMEOUT);
        this._restoreCommand$.next();
        return of([] as BuyerSurveyResponse[]);
      })
    );

    const surveys$ = iif(
      () => this._auth.userType === 'supplier',
      supplier$,
      buyer$
    ).pipe(
      catchError((error) => {
        this._toastr.error(error.error.ps_api_response_message);
        return of([]);
      }),
      map(
        (surveys) =>
          new DashboardSurveyTransform(surveys, this._auth.userType, [])
      ),
      map((surveys) => surveys.listing()),
      map((surveys) => state.concat(surveys)),
      tap((_state) => {
        this._surveyIds = this._getSurveyIdsFromList(_state.surveys); // E.g., [101, 102, 103]
        // Subscribe to relevant surveys
        this._socketService.subscribeToSurveys(this._surveyIds);
        this._dashboardStateService.surveys.cache({
          surveys: _state.surveys,
        });
        this._dashboardStateService.complete();
        this._completed = _state;
        this._pending = DashboardState.empty();
        this._completedTabIndex = this._findTabIndex(
          _state.pagination.status as string
        );
      })
    );

    const cache$ = of(this._dashboardStateService.surveys.listing).pipe(
      map((surveys) => state.copyWith({ surveys })),
      tap(() => {
        this._useCache = false;
      })
    );

    return iif(() => this._useCache, cache$, surveys$);
  }

  private _findTabIndex(status: string): number {
    return this.tabState.tabs.findIndex(
      (tab) => String(tab.status) === String(status)
    );
  }

  private _hasTabChanged(): boolean {
    return (
      this.tabState.tabs[this._completedTabIndex]?.status !==
      this.tabState.tabs[this._dashboardStateService.lastTab]?.status
    );
  }

  private _params(state: DashboardState) {
    return {
      ...state.pagination.request(),
      ...state.filtering.request(),
      ...state.sorting.request(),
    };
  }

  private _getSurveysRequestContext(state: DashboardState): HttpContext {
    if (!this.enableBackEndSortFilter$.getValue()) {
      return new HttpContext();
    }

    if (
      !Object.keys(state.filtering.request()).length &&
      !Object.keys(state.sorting.request()).length
    ) {
      return new HttpContext();
    }

    return new HttpContext()
      .set(ALLOW_INTERRUPTION, true)
      .set(
        INTERRUPTION_GRACE_PERIOD,
        Constants.INTERRUPTION_GRACE_PERIOD['get-surveys']
      );
  }

  private _getSurveyIdsFromList(surveys: SurveyListing[]): number[] {
    return surveys.map((s) => s.surveyId);
  }

  private _count(
    pagination: DashboardPagination,
    filtering: DashboardFiltering
  ): Observable<Counters> {
    const isMultiBuyer$ = this._auth.loggedInAsMultiBuyer$;
    const filterParams = {
      ...filtering.request(),
    };
    const buyer$ = isMultiBuyer$.pipe(
      switchMap((isMultiBuyer) =>
        this._buyerApiService
          .getSurveyCounts(
            SurveyStatus.Live,
            pagination.page,
            pagination.search,
            pagination.buyerId,
            pagination.view.managers.serviceOperator,
            pagination.view.managers.buyer,
            isMultiBuyer,
            filterParams
          )
          .pipe(map((response) => response.total))
      )
    );

    const supplier$ = this._supplierApiService
      .getSupplierSurveyCounts(SurveyStatus.Live, 1, pagination.search)
      .pipe(map((response) => response.count));

    return iif(() => this._auth.userType === 'supplier', supplier$, buyer$);
  }

  private _sockets(): Observable<DashboardState> {
    const status$ = this._socketService.surveyStatusChange$.pipe(
      filter((data) => this._state.contains(data.surveyId)),
      tap((data) => this.tabState.statusChange(data)),
      map((data) => this._state.statusChange(data, this.tab))
    );

    const progress$ = this._socketService.buyerProgressChannelV3$.pipe(
      filter((data) => this._state.contains(data.surveyId)),
      map((data) => this._state.progressChange(data))
    );

    const supplierData$ = this._socketService.dashboardChannel$.pipe(
      filter(
        ({ supplierManagement }) =>
          this._auth.userType === 'supplier' &&
          this._auth.user?.cmp === supplierManagement.supplier_id
      ),
      map(({ supplierManagement }) => ({
        surveyId: supplierManagement.survey_id,
        fielded: supplierManagement.fielded,
      }))
    );

    const surveyData$ = this._socketService.dashboardChannel$.pipe(
      filter(() => this._auth.userType !== 'supplier'),
      map(({ surveyManagement }) => ({
        surveyId: surveyManagement.survey_id,
        currentIR: surveyManagement.incidence_current,
        averageCpi: surveyManagement.averageCpi,
        fielded: surveyManagement.fielded,
      }))
    );

    const surveyScore$ = this._socketService.surveyScoreChange$.pipe(
      filter((data) => this._state.contains(data.surveyId)),
      map((data) => this._state.surveyScoreChange(data))
    );

    const dashData$ = merge(supplierData$, surveyData$).pipe(
      filter((data) => this._state.contains(data.surveyId)),
      map((data) => this._state.dataChange(data))
    );

    return merge(status$, progress$, surveyScore$, dashData$);
  }

  private _checkTOS(): void {
    const user = this._auth.user;
    const tosAccepted = this._auth?.user?.tos?.accept;
    const internalUser = user && user.eml.includes('@purespectrum.com');
    if (!tosAccepted && !internalUser) {
      this._modal.open(TosComponent);
    }
  }

  private _generateTabs(): void {
    const tabs = new TabsFactory(
      this._auth.userType,
      this._auth.buyerConfig?.enableInvoiceTab ?? false,
      this._auth.isLoggedInAsServiceBuyer(),
      this._auth.isLoggedInAsMasterMultiBuyer()
    ).create();

    this.tabState = new TabState(tabs);
  }

  private _updateView(): Subscription {
    return this._viewCommand$
      .pipe(
        switchMap((view) =>
          this._userService.setUserViewSelection(this._auth.user?.id, {
            option: view.option,
            pmId: [
              USER_DASHBOARD_VIEW_OBJECT.VIEW_BY_PM,
              USER_DASHBOARD_VIEW_OBJECT.VIEW_MY_PROJECTS_BUYER,
              USER_DASHBOARD_VIEW_OBJECT.VIEW_UNASSIGNED,
            ].includes(view.option)
              ? view.managers.buyer
              : view.managers.serviceOperator,
          })
        )
      )
      .subscribe();
  }

  private _clone(): Subscription {
    return this._cloneCommand$
      .pipe(
        switchMap((id) => {
          const project = this._findProject(id);
          if (project) {
            const infoText = new CloneUnavailableTextResolver(project).text();
            this._toastr.info(infoText);
            return of(null);
          }

          // Check if it's a multi-buyer survey and handle context accordingly before cloning
          const isMultiBuyer =
            this._auth.isLoggedInAsMultiBuyer() &&
            !this._auth.selectedMultiBuyerCompany;

          return (
            isMultiBuyer
              ? this._buyerApiService.getSurvey(id, 0, false).pipe(
                  map((survey: any) =>
                    typeof survey?.buyer_company === 'number'
                      ? survey.buyer_company
                      : undefined
                  ),
                  switchMap((multiBuyerCmpId) => {
                    if (multiBuyerCmpId !== undefined) {
                      return from(
                        this._multiBuyerService.selectBuyerPromise(
                          this._auth.user?.cmp!,
                          multiBuyerCmpId
                        )
                      ).pipe(map(() => multiBuyerCmpId));
                    } else {
                      return of(undefined);
                    }
                  })
                )
              : of(undefined)
          ).pipe(
            switchMap(() =>
              this._buyerApiService
                .cloneSurvey(id, this._auth.isLoggedInAsServiceBuyer())
                .pipe(
                  catchError((error) => {
                    let errorMessage =
                      'Error encountered while attempting to clone this survey.';
                    if (error?.error?.ps_api_response_message) {
                      errorMessage = error?.error?.ps_api_response_message;
                    }
                    this._toastr.error(errorMessage);
                    return of(null);
                  })
                )
            )
          );
        }),
        filter((value) => !!value),
        map((value) => value as BuyerSurveyResponse)
      )
      .subscribe((response) => {
        const clonedSurveyId = response.ps_survey_id;
        const manualOverrideTurnedOff = !!response.manual_override_turned_off;
        const inactiveQualificationMessage =
          new InactiveQualificationsMessageComposer(
            response.inactive_qualifications,
            'clone-survey'
          ).compose();
        if (inactiveQualificationMessage) {
          this._toastr.warning(inactiveQualificationMessage, '', {
            enableHtml: true,
          });
        }
        this._router.navigate([`/surveys/${clonedSurveyId}/edit`], {
          state: {
            isCloneSurvey: true,
            is_race_qualification_removed:
              response.is_race_qualification_removed,
            manualOverrideTurnedOff,
          },
        });
      });
  }

  private _tc(): Subscription {
    return this._tcCommand$
      .pipe(
        switchMap((action) =>
          forkJoin({
            survey: this._buyerApiService.getSurvey(action.id),
            tcs: this._buyerApiService.getTrafficChannelData(action.id),
            quotas: this._buyerApiService.getQuotaDetails(action.id, {
              page: 1,
              limit: Constants.QUOTAPAGELIMIT,
              search: '',
            }),
          }).pipe(map((response) => ({ ...action, response })))
        ),
        map((tc) => {
          const survey = this._surveyPayloadService.mapBuyerSurvey(
            tc.response.survey
          );
          return {
            id: tc.id,
            type: tc.type,
            survey: survey,
            surveyQuotas: tc.response.quotas.quota,
            tcSurveys: tc.response.tcs,
          };
        }),
        switchMap((data) =>
          this._modal
            .open(TrafficChannelModalComponent, {
              data,
              width: '60%',
            })
            .onClose$.pipe(map((msg) => ({ msg, id: data.id })))
        ),
        filter((response) => response.msg === 'Created')
      )
      .subscribe((response) =>
        this._toastr.success(
          `${notifyMessage.TRAFFIC_CHANNEL_SURVEY_CREATED_SUBPART} ${response.id}`
        )
      );
  }

  private _download(): Subscription {
    return this._downloadCommand$
      .pipe(
        switchMap((id) =>
          this._buyerService.downloadTransactionReport(id, false).pipe(
            catchError(() => {
              this._toastr.error(
                notifyMessage.errorMessage.DOWNLOAD_REPORT_ERROR
              );
              return of(null);
            })
          )
        ),
        filter((value) => !!value)
      )
      .subscribe(() => console.log('success'));
  }

  private _downloadBuyerBulkReport() {
    return this._bulkReportCommnad$
      .pipe(
        filter((report) => report.type === 'buyer'),
        map((report) => report as BuyerCombinedReport),
        switchMap((report) =>
          this._buyerService
            .downloadCombinedReport(
              report.surveys,
              report.testTransactions,
              report.transStatus
            )
            .pipe(
              catchError((e) => {
                const error =
                  e || notifyMessage.errorMessage.DOWNLOAD_REPORT_ERROR;
                this._toastr.error(error);
                return of(null);
              })
            )
        )
      )
      .subscribe();
  }

  private _downloadSupplierBulkReport() {
    return this._bulkReportCommnad$
      .pipe(
        filter((report) => report.type === 'supplier'),
        map((report) => report as SupplierCombinedReport),
        switchMap((report) =>
          this._reportService
            .requestMultiSurveysStatementReport(
              this._auth.email(),
              report.surveys
            )
            .pipe(
              catchError((e) => {
                const message =
                  e && e.error
                    ? e.error.error
                    : notifyMessage.errorMessage.DOWNLOAD_REPORT_ERROR;
                return of({ status: 'Error', message });
              })
            )
        )
      )
      .subscribe((result) => {
        if (result.status !== 'Success') {
          this._toastr.error(result.message);
        }

        this._toastr.success(result.message);
      });
  }

  private _findProject(id: number): SurveyListing | undefined {
    return this._state?.surveys?.find(
      (survey) =>
        survey.surveyId === id &&
        Object.values(PROJECT_TYPES).includes(survey.project_type!)
    );
  }

  private _updateBackEndSortFilter() {
    return this._companyAndServiceBuyer$
      .pipe(
        map(({ company }) => {
          return (
            !!this._auth.buyerConfig?.enableBackEndSortFilter ||
            (this._auth.userType === 'operator' && !!company)
          );
        }),
        startWith(!!this._auth.buyerConfig?.enableBackEndSortFilter),
        distinctUntilChanged()
      )
      .subscribe((value) => {
        this.enableBackEndSortFilter$.next(value);
      });
  }

  private _clearState() {
    return this._auth.loggedInAsServiceBuyer$
      .pipe(
        skip(1),
        map((loggedIn) => ({ loggedIn, company: this._auth.serviceBuyer?.id })),
        distinctUntilChanged(
          (previous, current) =>
            previous.loggedIn === current.loggedIn &&
            previous.company === current.company
        )
      )
      .subscribe(({ loggedIn }) => {
        // Clear the company when the service buyer logs in and the company (buyer account) is set
        if (loggedIn && this._dashboardStateService.company) {
          this._companyCommand$.next(undefined);
          this._dashboardStateService.company = undefined;
        }

        // Clear the search, sort and filter when the service buyer logs in/out
        this._dashboardStateService.clearState(true);
      });
  }

  private _resetState(): Observable<DashboardState> {
    const dashboardState: TableStateEvent = {
      sort: { type: 'none', field: '' },
      filter: [],
    };

    const buyerFilter = this._dashboardStateService.findFilter(
      Constants.SURVEY_LISTING_TABLE_HEADERS.BUYER_ACCOUNT
    );

    if (buyerFilter) {
      // Keep the buyer filter
      dashboardState.filter.push(buyerFilter);
    }

    this._dashboardStateService.update(dashboardState);

    const newState = this._pending.copyWith({
      sorting: DashboardSorting.empty(),
      filtering: DashboardFiltering.empty(),
    });
    return of(newState);
  }

  private _revertState(): Observable<DashboardState> {
    const completedState = this._completed;
    const dashboardState = this._dashboardStateService.completedState;

    const buyerFilter = this._dashboardStateService.findFilter(
      Constants.SURVEY_LISTING_TABLE_HEADERS.BUYER_ACCOUNT,
      true
    );

    if (buyerFilter) {
      // Re-apply the buyer filter
      this._dashboardStateService.company = completedState.company;
      this._companyCommand$.next(completedState.company);
    }

    this._dashboardStateService.update(dashboardState);
    this.resetScroll.next();

    return of(completedState);
  }

  private _resetPaginationIfNotFirstPage() {
    if (this._state.pagination.page > 1) {
      this._paginationCommand$.next(DashboardPagination.first());
      this.resetScroll.next();
    }
  }
}
